import { MentionsBreakdownWithInteractionsItem, MentionsStatsBreakdownItem, PolarityValue } from '@shared/models'

import { Tokens } from '@/assets/tokens'
import { SkeletonLoader } from '@/components/derived'
import { formatNumberBR } from '@/utils/numbers'

import { StatCard } from '@/pages/CompanyDetails/channels/components/stats/Stats'
import { useMentionsContext } from '../../Mentions.context'
import {
  BreakdownFill,
  BreakdownHeader,
  BreakdownLabel,
  BreakdownList,
  BreakdownRow,
  BreakdownsGrid,
  BreakdownSection,
  BreakdownTitle,
  BreakdownTrack,
  BreakdownValue,
  ChannelMetricRow,
  ChannelRow,
  DashboardContainer,
  EmptyState,
  EngagementGrid,
  MetricLabel,
} from './Dashboard.styles'

const SENTIMENT_COLORS: Record<string, string> = {
  [PolarityValue.POSITIVE]: Tokens.ColorUISuccess,
  [PolarityValue.FAVORABLE]: Tokens.ColorUIPrimaryLight,
  [PolarityValue.NEGATIVE]: Tokens.ColorUIDanger,
  [PolarityValue.UNFAVORABLE]: `${Tokens.ColorUIDanger}66`,
  [PolarityValue.NEUTRAL]: Tokens.ColorTextTertiary,
  [PolarityValue.NO_RULE]: Tokens.ColorTextTertiary,
}

function colorForSentiment(key: string): string {
  return SENTIMENT_COLORS[key.trim().toUpperCase()] ?? Tokens.ColorTextTertiary
}

function Breakdown({
  title,
  items,
  colorFor,
}: {
  title: string
  items: MentionsStatsBreakdownItem[]
  colorFor: (key: string) => string
}) {
  return (
    <BreakdownSection>
      <BreakdownTitle>{title}</BreakdownTitle>
      {items.length === 0 ?
        <EmptyState>Sem dados para os filtros selecionados</EmptyState>
      : <BreakdownList>
          {items.map((item) => (
            <BreakdownRow key={item.key}>
              <BreakdownHeader>
                <BreakdownLabel title={item.key}>{item.key}</BreakdownLabel>
                <BreakdownValue>
                  {formatNumberBR(item.count)} · {item.percentage}%
                </BreakdownValue>
              </BreakdownHeader>
              <BreakdownTrack>
                <BreakdownFill $percentage={item.percentage} $color={colorFor(item.key)} />
              </BreakdownTrack>
            </BreakdownRow>
          ))}
        </BreakdownList>
      }
    </BreakdownSection>
  )
}

function BreakdownWithInteractions({
  title,
  items,
  colorFor,
}: {
  title: string
  items: MentionsBreakdownWithInteractionsItem[]
  colorFor: (key: string) => string
}) {
  return (
    <BreakdownSection>
      <BreakdownTitle>{title}</BreakdownTitle>
      {items.length === 0 ?
        <EmptyState>Sem dados para os filtros selecionados</EmptyState>
      : <BreakdownList>
          {items.map((item) => (
            <ChannelRow key={item.key}>
              <BreakdownLabel title={item.key}>{item.key}</BreakdownLabel>

              <ChannelMetricRow>
                <BreakdownHeader>
                  <MetricLabel>Volume</MetricLabel>
                  <BreakdownValue>
                    {formatNumberBR(item.count)} · {item.percentage}%
                  </BreakdownValue>
                </BreakdownHeader>
                <BreakdownTrack>
                  <BreakdownFill $percentage={item.percentage} $color={colorFor(item.key)} />
                </BreakdownTrack>
              </ChannelMetricRow>

              {item.interactions > 0 && (
                <ChannelMetricRow>
                  <BreakdownHeader>
                    <MetricLabel>Interações</MetricLabel>
                    <BreakdownValue>
                      {formatNumberBR(item.interactions)} · {item.interactionsPercentage}%
                    </BreakdownValue>
                  </BreakdownHeader>
                  <BreakdownTrack>
                    <BreakdownFill $percentage={item.interactionsPercentage} $color={Tokens.ColorUIInfo} />
                  </BreakdownTrack>
                </ChannelMetricRow>
              )}

              {item.reach > 0 && (
                <ChannelMetricRow>
                  <BreakdownHeader>
                    <MetricLabel>Alcance</MetricLabel>
                    <BreakdownValue>
                      {formatNumberBR(item.reach)} · {item.reachPercentage}%
                    </BreakdownValue>
                  </BreakdownHeader>
                  <BreakdownTrack>
                    <BreakdownFill $percentage={item.reachPercentage} $color={Tokens.ColorUIPurple} />
                  </BreakdownTrack>
                </ChannelMetricRow>
              )}
            </ChannelRow>
          ))}
        </BreakdownList>
      }
    </BreakdownSection>
  )
}

export function Dashboard() {
  const { stats, isLoadingStats } = useMentionsContext()

  if (isLoadingStats) {
    return <SkeletonLoader />
  }

  if (!stats || stats.total === 0) {
    return <EmptyState>Nenhuma menção encontrada para os filtros selecionados</EmptyState>
  }

  return (
    <DashboardContainer>
      <EngagementGrid>
        <StatCard label="Total de Menções" value={formatNumberBR(stats.total)} color="default" />
        <StatCard label="Curtidas" value={formatNumberBR(stats.engagement.likes)} color="success" />
        <StatCard label="Compartilhamentos" value={formatNumberBR(stats.engagement.shares)} color="warning" />
        <StatCard label="Total de Interações" value={formatNumberBR(stats.engagement.totalInteractions)} color="default" />
        <StatCard label="Alcance" value={formatNumberBR(stats.engagement.reach)} color="default" />
      </EngagementGrid>

      <BreakdownsGrid>
        <BreakdownWithInteractions title="Sentimento" items={stats.sentiment} colorFor={colorForSentiment} />
        <Breakdown title="Tiers" items={stats.tiers} colorFor={() => Tokens.ColorUIPrimary} />
        <BreakdownWithInteractions title="Canais" items={stats.channels} colorFor={() => Tokens.ColorUIPrimary} />
        <Breakdown title="Temas" items={stats.themes} colorFor={() => Tokens.ColorUIPrimary} />
      </BreakdownsGrid>
    </DashboardContainer>
  )
}
